import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import i18n from 'i18next';

import { MetricValueLink } from '@/components/atoms/MetricValueLink';
import { OverviewValueWithTrendTooltip } from '@/components/molecules/OverviewValueWithTrendTooltip';
import type { LocationType } from '@/domain/client';
import { shouldShowBetaLaksvelOverviewCell } from '@/routes/Client/Cage/pages/Health/_utils/overviewHealthColumnVisibility';
import { formatDate } from '@/utils/date';
import { formatNumberByLocale, formatUniversalUnitByLocale } from '@/utils/localeFormatters';
import { createCageUrl } from '@/utils/urlGenerator';

import LocationOverviewCageLinkCell from './cells/LocationOverviewCageLinkCell';
import LocationOverviewFishGroupCell from './cells/LocationOverviewFishGroupCell';
import LocationOverviewHistoricalCell from './cells/LocationOverviewHistoricalCell';
import LocationOverviewLaksvelCell from './cells/LocationOverviewLaksvelCell';
import LocationOverviewSwimSpeedCell from './cells/LocationOverviewSwimSpeedCell';
import LocationOverviewWoundsCell from './cells/LocationOverviewWoundsCell';
import { penLabelKey } from './penLabel';
import type { LocationOverviewColumnKey, LocationOverviewRow } from './types';

/** Legacy `'---'` placeholder, kept so the table reads the same when data is missing. */
export const NO_VALUE = '---';

/** Lice grow slowly, so a smaller slope already counts as a trend. */
const LICE_GROWTH_LIMIT = 0.05;

/** What every cell of the table reads besides its row. */
export interface OverviewCellContext {
    clientId: number;
    locationId: number;
    locationType: LocationType;
    isHealthModuleEnabled: boolean;
    isBehaviorModuleEnabled: boolean;
    markHistorical: (cageId: number, historical: boolean) => Promise<unknown>;
    /** Whether the Name column shows; without it the link button carries the pen name. */
    showsNameColumn: boolean;
    t: TFunction;
}

/** The cells that show, and link to, a pen's measurements. */
const METRIC_KEYS = new Set<LocationOverviewColumnKey>([
    'weight',
    'growth',
    'lice',
    'laksvel',
    'wound',
    'speed',
    'measurement',
]);

type CageRow = Extract<LocationOverviewRow, { cage: unknown }>;

/** One cell of a pen's row. */
export function renderCageCell(
    key: LocationOverviewColumnKey,
    row: CageRow,
    context: OverviewCellContext
): ReactNode {
    const { cage, status } = row;
    const { clientId, locationId, locationType, t } = context;
    const cageUrl = createCageUrl(clientId, locationId, cage.cageId);
    const weight = cage.cageData.weight;
    const growth = weight?.growth;
    const lice = cage.cageData.lice;

    // A pen with no fish group has no data to show from the fish groups' point of view, and the
    // pen pages send a reader who arrives without a group back to the location. So the row
    // offers no way in: the button is disabled and the metrics are plain text.
    //
    // `null`, not `undefined`: only a response that asked for fish groups says a pen has none.
    // Without the flag, or before the flagged overview lands, the field is absent and the row
    // keeps its links. A historical pen keeps them too: it predates fish groups, and its pages
    // show the cage's data without one.
    const hasNoFishGroup = cage.activeFishGroup === null && status !== 'historical';
    if (hasNoFishGroup && METRIC_KEYS.has(key)) {
        return NO_VALUE;
    }

    switch (key) {
        case 'cage':
            return (
                <LocationOverviewCageLinkCell
                    cage={cage}
                    locationType={locationType}
                    clientId={clientId}
                    locationId={locationId}
                    showCageName={!context.showsNameColumn}
                    disabled={hasNoFishGroup}
                />
            );
        case 'name':
            return cage.cageName;
        case 'fishGroup':
            return (
                <LocationOverviewFishGroupCell
                    name={cage.activeFishGroup?.name}
                    fromDate={cage.activeFishGroup?.fromDate}
                    toDate={null}
                    // Only the pens the backend returns as historical. An active or inactive
                    // pen with no cohort simply has none: `---`.
                    historicalCage={status === 'historical'}
                    penLabel={penLabelKey(locationType)}
                />
            );
        case 'weight':
            return (
                <MetricValueLink to={`${cageUrl}/weight`}>
                    {weight?.livingWeight
                        ? formatUniversalUnitByLocale(weight.livingWeight, 'g', i18n.language, 0)
                        : NO_VALUE}
                </MetricValueLink>
            );
        case 'growth':
            return growth || growth === 0
                ? `${formatNumberByLocale(growth, i18n.language, 0)} g/${t('daypartTag.day')}`
                : NO_VALUE;
        case 'lice':
            return (
                <OverviewValueWithTrendTooltip
                    value={lice?.femaleLegacyAvg}
                    growth={lice?.femaleLegacyAvgDiff}
                    growthLimit={LICE_GROWTH_LIMIT}
                    showPercentage={false}
                    to={`${cageUrl}/lice`}
                />
            );
        case 'laksvel':
            return shouldShowBetaLaksvelOverviewCell(
                context.isHealthModuleEnabled,
                cage.bioscopeVersion
            ) ? (
                <LocationOverviewLaksvelCell
                    cage={cage}
                    clientId={clientId}
                    locationId={locationId}
                />
            ) : (
                NO_VALUE
            );
        case 'wound':
            return (
                <LocationOverviewWoundsCell
                    cage={cage}
                    clientId={clientId}
                    locationId={locationId}
                    isHealthModuleEnabled={context.isHealthModuleEnabled}
                />
            );
        case 'speed':
            return (
                <LocationOverviewSwimSpeedCell
                    cage={cage}
                    clientId={clientId}
                    locationId={locationId}
                    isBehaviorModuleEnabled={context.isBehaviorModuleEnabled}
                />
            );
        case 'measurement':
            return weight?.day ? formatDate(weight.day, 'display') : NO_VALUE;
        case 'historical':
            return (
                <LocationOverviewHistoricalCell
                    cage={cage}
                    markHistorical={context.markHistorical}
                />
            );
    }
}
